import React, { useCallback, useEffect, useLayoutEffect, useRef } from 'react'
import clsx from 'clsx'
import PropTypes from 'prop-types'
import styles from 'components/Drawer/subcomponents/DrawerFooter/subcomponents/DrawerFooterAnimatedIcon/drawer-footer-animated-icon.module.scss'
import userSvg from 'images/drawer-footer-icons/user.js'
import messagesSvg from 'images/drawer-footer-icons/messages.js'
import questionSvg from 'images/drawer-footer-icons/question.js'
import settingsSvg from 'images/drawer-footer-icons/settings.js'

const ICONS = {
  user: userSvg,
  messages: messagesSvg,
  question: questionSvg,
  settings: settingsSvg,
}

const SMIL_SELECTOR = 'animate, animateTransform, animateMotion'

const SHADOW_STYLES =
  '<style>:host{display:block;width:100%;height:100%}svg{display:block;width:100%;height:100%;overflow:visible}</style>'

const getSvgElement = (host) => host?.shadowRoot?.querySelector('svg')

const getSmilNodes = (svg) => (svg ? Array.from(svg.querySelectorAll(SMIL_SELECTOR)) : [])

const settleToRestFrame = (svg) => {
  if (!svg) return

  svg.pauseAnimations()
  svg.setCurrentTime(0)

  getSmilNodes(svg).forEach((node) => {
    try {
      node.beginElement()
    } catch (_) {
      // SMIL beginElement may throw in unsupported browsers
    }
  })

  svg.setCurrentTime(0)
  svg.pauseAnimations()
}

const mountSvgMarkup = (host, markup) => {
  if (!host) return null

  const shadow = host.shadowRoot ?? host.attachShadow({ mode: 'open' })
  shadow.innerHTML = `${SHADOW_STYLES}${markup}`

  const svg = getSvgElement(host)
  settleToRestFrame(svg)
  return svg
}

const playSvgAnimations = (svg) => {
  if (!svg) return

  svg.pauseAnimations()
  svg.setCurrentTime(0)

  getSmilNodes(svg).forEach((node) => {
    try {
      node.beginElement()
    } catch (_) {
      // SMIL beginElement may throw in unsupported browsers
    }
  })

  svg.unpauseAnimations()
}

const DrawerFooterAnimatedIcon = ({ name, className }) => {
  const containerRef = useRef(null)
  const markup = ICONS[name]

  const resetIcon = useCallback(() => {
    mountSvgMarkup(containerRef.current, markup)
  }, [markup])

  useLayoutEffect(() => {
    resetIcon()
  }, [resetIcon])

  useEffect(() => {
    const host = containerRef.current
    if (!host) return

    const trigger = host.closest('button, a, [role="button"]')
    if (!trigger) return

    const handleMouseEnter = () => playSvgAnimations(getSvgElement(host))
    const handleMouseLeave = () => resetIcon()

    trigger.addEventListener('mouseenter', handleMouseEnter)
    trigger.addEventListener('mouseleave', handleMouseLeave)

    return () => {
      trigger.removeEventListener('mouseenter', handleMouseEnter)
      trigger.removeEventListener('mouseleave', handleMouseLeave)
    }
  }, [resetIcon])

  if (!markup) return null

  return (
    <span
      ref={containerRef}
      aria-hidden
      className={clsx(styles.icon, className)}
    />
  )
}

export default DrawerFooterAnimatedIcon

DrawerFooterAnimatedIcon.propTypes = {
  name: PropTypes.oneOf(['user', 'messages', 'question', 'settings']).isRequired,
  className: PropTypes.string,
}
